import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import store from 'store';
import config from 'config';
import { logRequest, logResponse } from 'utils/client';
import { getExtendedTimeout, getUserMessage } from 'utils/request';
import {
  buildRejectionError,
  handleIdempotencyKeyConflict,
  handleIdempotencyKeyRequired,
  handlePaymentMethodUpgradeRequired,
  isIdempotencyKeyConflictError,
  isIdempotencyKeyRequiredError,
  isPaymentMethodUpgradeRequired,
  isReportableRejection,
  rejectWrongResponse,
  shouldRejectWrongResponse,
} from 'api/client/middleware.utils';
import { ApiClientError } from 'api/client/index';
import DataLayer from 'app/utils/dataLayer';
import Logger from 'utils/logger';
import RequestHeaders from 'app/constants/requestHeaders';
import { CognitoTokenManager } from 'app/modules/auth';
import { TokenRefreshService } from 'app/modules/auth/utils/tokenRefreshService';

const stripQuery = (url?: string) => url?.split('?')[0];

const toRequestSummary = (
  requestConfig?: Pick<AxiosRequestConfig, 'method' | 'url'>,
) => ({
  method: requestConfig?.method,
  url: stripQuery(requestConfig?.url),
});

export function requestLogger(request: AxiosRequestConfig) {
  const info = `${new Date().toLocaleTimeString()} | Request: ${request.method?.toUpperCase()} | ${
    request.url
  } | Sent data:`;

  let serializedData;

  if (request.data instanceof FormData) {
    serializedData = Object.fromEntries(Array.from(request.data));
  }

  logRequest(info, serializedData ?? request.data);

  return request;
}

export function responseLogger(response: AxiosResponse) {
  const info = `${new Date().toLocaleTimeString()} | Response: ${
    response.status
  } | ${response.config.url}| Response data:`;

  logResponse(info, response.data);

  return response;
}

// If sent formData contains files extend timeout of request
export function timeoutExtender(request: AxiosRequestConfig) {
  if (request.data instanceof FormData) {
    for (const item of Array.from(request.data)) {
      if (item[1] instanceof File) {
        if (
          typeof request.timeout === 'number' &&
          request.timeout > getExtendedTimeout()
        ) {
          break;
        }

        request.timeout = getExtendedTimeout();
        break;
      }
    }
  }

  return request;
}

export function responseChecker(response: AxiosResponse) {
  if (shouldRejectWrongResponse(response)) {
    DataLayer.push({
      event: DataLayer.events.REJECTED_REQUEST_RESPONSE,
      status: response.status,
      ...toRequestSummary(response.config),
    });
    if (isReportableRejection(response)) {
      Logger.error(buildRejectionError(response), {
        url: response.config?.url,
        status: response.status,
      });
    }

    return rejectWrongResponse(response);
  }

  if (
    response.data?.data &&
    (response.data?.pagination || response.data?.meta)
  ) {
    return response.data as ApiResponse;
  }

  return response.data?.data !== undefined ? response.data.data : response.data;
}

export function rejectExtender(axiosError: AxiosError) {
  // 409 payment_method_upgrade_required wall — must run BEFORE
  // shouldRejectWrongResponse so the standard 4xx-as-error path doesn't
  // claim this case first. The handler opens the Stripe attach modal,
  // replays the original request on success, or rejects with a distinct
  // ApiClientError name on cancel.
  if (
    axiosError.response &&
    isPaymentMethodUpgradeRequired(axiosError.response)
  ) {
    return handlePaymentMethodUpgradeRequired(axiosError);
  }

  if (
    axiosError.response &&
    isIdempotencyKeyRequiredError(axiosError.response)
  ) {
    return handleIdempotencyKeyRequired(axiosError);
  }

  if (
    axiosError.response &&
    isIdempotencyKeyConflictError(axiosError.response)
  ) {
    return handleIdempotencyKeyConflict(axiosError);
  }

  if (axiosError.response && shouldRejectWrongResponse(axiosError.response)) {
    DataLayer.push({
      event: DataLayer.events.REJECTED_REQUEST_RESPONSE,
      status: axiosError.response.status,
      ...toRequestSummary(axiosError.response.config),
    });
    if (isReportableRejection(axiosError.response)) {
      Logger.error(buildRejectionError(axiosError.response), {
        url: axiosError.response.config?.url,
        status: axiosError.response.status,
      });
    }
    return rejectWrongResponse(axiosError.response, axiosError);
  }

  if (axiosError.code === 'ECONNABORTED') {
    DataLayer.push({
      event: DataLayer.events.TIMEOUT_REQUEST_RESPONSE,
      ...toRequestSummary(axiosError.config),
    });
  }

  return Promise.reject(
    new ApiClientError({
      userMessage: getUserMessage(axiosError.response),
      axiosError,
      name: axiosError.name,
      message: axiosError.message,
      stack: axiosError.stack,
    }),
  );
}

/**
 * Waits out a token refresh that is already in flight, and forces one when the
 * stored access token has already expired.
 *
 * Without this, a "cold" page load races itself: the app kicks off
 * `POST /cognito/refresh`, the first data queries fire in the same tick, read
 * the not-yet-replaced token out of the store, and come back 4xx. Those queries
 * default to `retry: false`, so a single lost race renders a terminal error
 * page — most visibly "Couldn't load your billing details" — even though the
 * token is valid a few hundred milliseconds later.
 *
 * Deliberately not gated on any one endpoint: the race is a property of the
 * auth layer, and any request leaving during that window is equally doomed.
 * Failures are swallowed — if the refresh can't complete, the request proceeds
 * with whatever token exists and the normal 401 path handles it.
 */
async function awaitUsableAuthToken(): Promise<void> {
  const pending = TokenRefreshService.getPendingRefresh();

  if (pending) {
    await pending.catch(() => undefined);
    return;
  }

  if (TokenRefreshService.isAccessTokenExpired()) {
    await TokenRefreshService.refresh().catch(() => undefined);
  }
}

export async function authJoiner(request: AxiosRequestConfig) {
  CognitoTokenManager.validateAccountDomain();

  await awaitUsableAuthToken();

  const authToken = store.getState().app.authToken;
  const accountId = CognitoTokenManager.getSelectedAccountId();

  Logger.log(
    `[Auth][AuthJoiner] ${request.method?.toUpperCase()} ${
      request.url
    } (baseURL: ${request.baseURL}) | authToken=${
      authToken ? 'present' : 'null'
    }, accountId=${accountId}`,
  );

  function getHeaders() {
    const baseHeaders = { ...request.headers };

    if (accountId) {
      baseHeaders[RequestHeaders.AccountId] = String(accountId);
    }

    if (authToken && !request.headers?.[RequestHeaders.Authorization]) {
      baseHeaders[RequestHeaders.Authorization] = `Bearer ${authToken}`;
      if (request.baseURL === config.KONTENTINO_BLOG_WORDPRESS_API_URL) {
        return {
          ...request.headers,
          [RequestHeaders.KontentinoBlogWordpressBasicAuthentication]:
            'Basic ' +
            btoa(
              config.KONTENTINO_BLOG_WORDPRESS_USERNAME +
                ':' +
                config.KONTENTINO_BLOG_WORDPRESS_PASSWORD,
            ),
        };
      }

      if (
        [
          config.ANALYTICS_ENDPOINT,
          config.SOCIAL_LOGIN_SERVICE_ENDPOINT,
          config.REPORTS_API_ENDPOINT,
        ].some((baseUrl) => baseUrl === request.baseURL) &&
        !request.headers[RequestHeaders.Authorization]
      ) {
        return {
          ...baseHeaders,
          [RequestHeaders.Authorization]: `Bearer ${authToken}`,
        };
      }

      if (typeof request.headers?.[RequestHeaders.HttpJwtToken] === 'string') {
        return baseHeaders;
      }

      return {
        ...baseHeaders,
        [RequestHeaders.HttpJwtToken]: authToken,
      };
    }

    if (!authToken) {
      Logger.warn(
        `[Auth][AuthJoiner] No auth token for request: ${request.method?.toUpperCase()} ${
          request.url
        }`,
      );
    }

    return baseHeaders;
  }

  request.headers = getHeaders();

  return request;
}
